/**
 * @typedef {import('micromark-util-types').Event} Event
 */ // Port of `edit_map.rs` from `markdown-rs`.
// This should move to `markdown-js` later.
// Deal with several changes in events, batching them together.
//
// Preferably, changes should be kept to a minimum.
// Sometimes, it’s needed to change the list of events, because parsing can be
// messy, and it helps to expose a cleaner interface of events to the compiler
// and other users.
// It can also help to merge many adjacent similar events.
// And, in other cases, it’s needed to parse subcontent: pass some events
// through another tokenizer and inject the result.
/**
 * @typedef {[number, number, Array<Event>]} Change
 * @typedef {[number, number, number]} Jump
 */ /**
 * Tracks a bunch of edits.
 */ function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _class_call_check(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _create_class(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
export var EditMap = /*#__PURE__*/ function() {
    "use strict";
    function EditMap() {
        _class_call_check(this, EditMap);
        /**
     * Record of changes.
     *
     * @type {Array<Change>}
     */ this.map = [];
    }
    _create_class(EditMap, [
        {
            /**
   * Create an edit: a remove and/or add at a certain place.
   *
   * @param {number} index
   * @param {number} remove
   * @param {Array<Event>} add
   * @returns {void}
   */ key: "add",
            value: function add(index, remove, add) {
                addImpl(this, index, remove, add);
            }
        },
        {
            // To do: not used here.
            // /**
            //  * Create an edit: but insert `add` before existing additions.
            //  *
            //  * @param {number} index
            //  * @param {number} remove
            //  * @param {Array<Event>} add
            //  * @returns {void}
            //  */
            // addBefore(index, remove, add) {
            //   addImpl(this, index, remove, add, true)
            // }
            /**
   * Done, change the events.
   *
   * @param {Array<Event>} events
   * @returns {void}
   */ key: "consume",
            value: function consume(events) {
                this.map.sort(function(a, b) {
                    return a[0] - b[0];
                });
                /* c8 ignore next 3 -- `resolve` is never called without tables, so without edits. */ if (this.map.length === 0) {
                    return;
                }
                // To do: if links are added in events, like they are in `markdown-rs`,
                // this is needed.
                // // Calculate jumps: where items in the current list move to.
                // /** @type {Array<Jump>} */
                // const jumps = []
                // let index = 0
                // let addAcc = 0
                // let removeAcc = 0
                // while (index < this.map.length) {
                //   const [at, remove, add] = this.map[index]
                //   removeAcc += remove
                //   addAcc += add.length
                //   jumps.push([at, removeAcc, addAcc])
                //   index += 1
                // }
                //
                // . shiftLinks(events, jumps)
                var index = this.map.length;
                /** @type {Array<Array<Event>>} */ var vecs = [];
                while(index > 0){
                    index -= 1;
                    vecs.push(events.slice(this.map[index][0] + this.map[index][1]));
                    // eslint-disable-next-line unicorn/no-array-push-push
                    vecs.push(this.map[index][2]);
                    // Truncate rest.
                    events.length = this.map[index][0];
                }
                vecs.push(_to_consumable_array(events));
                events.length = 0;
                var slice = vecs.pop();
                while(slice){
                    var _events;
                    (_events = events).push.apply(_events, _to_consumable_array(slice));
                    slice = vecs.pop();
                }
                // Truncate everything.
                this.map.length = 0;
            }
        }
    ]);
    return EditMap;
}();
/**
 * Create an edit.
 *
 * @param {EditMap} editMap
 * @param {number} at
 * @param {number} remove
 * @param {Array<Event>} add
 * @returns {void}
 */ function addImpl(editMap, at, remove, add) {
    var index = 0;
    /* c8 ignore next 3 -- `resolve` is never called without tables, so without edits. */ if (remove === 0 && add.length === 0) {
        return;
    }
    while(index < editMap.map.length){
        if (editMap.map[index][0] === at) {
            var // To do: before not used.
            // if (before) {
            //   add.push(...editMap.map[index][2])
            //   editMap.map[index][2] = add
            // } else {
            _editMap_map_index_;
            editMap.map[index][1] += remove;
            (_editMap_map_index_ = editMap.map[index][2]).push.apply(_editMap_map_index_, _to_consumable_array(add));
            // }
            return;
        }
        index += 1;
    }
    editMap.map.push([
        at,
        remove,
        add
    ]);
} // /**
 //  * Shift `previous` and `next` links according to `jumps`.
 //  *
 //  * This fixes links in case there are events removed or added between them.
 //  *
 //  * @param {Array<Event>} events
 //  * @param {Array<Jump>} jumps
 //  */
 // function shiftLinks(events, jumps) {
 //   let jumpIndex = 0
 //   let index = 0
 //   let add = 0
 //   let rm = 0
 //   while (index < events.length) {
 //     const rmCurr = rm
 //     while (jumpIndex < jumps.length && jumps[jumpIndex][0] <= index) {
 //       add = jumps[jumpIndex][2]
 //       rm = jumps[jumpIndex][1]
 //       jumpIndex += 1
 //     }
 //     // Ignore items that will be removed.
 //     if (rm > rmCurr) {
 //       index += rm - rmCurr
 //     } else {
 //       console.log('to do: links?', add, rmCurr)
 //       // ?
 //       // if let Some(link) = &events[index].link {
 //       //     if let Some(next) = link.next {
 //       //         events[next].link.as_mut().unwrap().previous = Some(index + add - rm);
 //       //         while jumpIndex < jumps.len() && jumps[jumpIndex].0 <= next {
 //       //             add = jumps[jumpIndex].2;
 //       //             rm = jumps[jumpIndex].1;
 //       //             jumpIndex += 1;
 //       //         }
 //       //         events[index].link.as_mut().unwrap().next = Some(next + add - rm);
 //       //         index = next;
 //       //         continue;
 //       //     }
 //       // }
 //       index += 1
 //     }
 //   }
 // }
